import React from 'react';
import { ShieldCheck, Target, Coins, Binary, Lock, Activity, AlertTriangle, ShieldAlert, XOctagon } from 'lucide-react';
import { gerarParecer } from './parecerEngine';
import { getSectorCycleBenchmark } from './financeEngine';
import { DISCLAIMER_FULL } from '../constants/constants';

const formatFix = (val) => {
   if (val === undefined || val === null || val === '') return "0,00";
   const num = typeof val === 'string' ? Number(val.replace(/\./g, '').replace(',', '.')) : Number(val);
   return isNaN(num) ? "0,00" : num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ⚠️ AQUI ESTÃO AS NOVAS VARIÁVEIS RECEBIDAS
export default function ReportEngine({
   data, userPlan, basicRef, superRef, insights = [], status,
   wlName, wlColor, wlLogo, isConsultor, isRealConsultor,
   apexData = null, auditId = null,
   // 🔥 NOVO: Projeção por IA Estocástica e Oportunidades Estratégicas —
   // antes só existiam na tela (Dashboard/app), nunca no PDF. O cliente
   // recebia um relatório sem a parte que mais mostra visão de futuro.
   projecaoEstocastica = [], leituraCurto = null, leituraMedio = null, leituraLongo = null,
   horizontesLiberados = [], oportunidadesIA = null
}) {
   const empresa = data?.companyName || "Empresa Não Informada";
   const segmento = data?.segment || "Não Informado";
   const ebitda = formatFix(data?.ebitda);

   // ==========================================
   // 🔒 SELO DE AUDITORIA (data/hora + código único do documento)
   // ==========================================
   const auditNow = new Date();
   const auditTimestamp = `${auditNow.toLocaleDateString('pt-BR')} às ${auditNow.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
   const auditCode = `PHX-${String(auditId || auditNow.getTime()).slice(-8).toUpperCase()}`;
   const AuditStamp = () => (
      <p style={{ fontSize: '7px', color: '#cbd5e1', margin: '2px 0 0 0', letterSpacing: '0.3px' }}>
         Documento gerado pelo Phoenix OS® em {auditTimestamp} — Auditoria Nº {auditCode}
      </p>
   );
   const margin = data?.margin || "0.0";
   const alavancagem = data?.alavancagem || "0.00";
   const valuation = formatFix(data?.valuation);
   const equity = formatFix(data?.equity);
   const score = data?.score || "0";

   const projectedEbitda = formatFix(data?.projectedEbitda);
   const simRevenue = data?.simRevenue || 0;
   const simCosts = data?.simCosts || 0;
   const safeDividends = formatFix(data?.safeDividends);
   const emergencyReserve = formatFix(data?.emergencyReserve);
   const ruptureRisk = data?.ruptureRisk || 0;

   // 🔥 CORREÇÃO: o relatório é uma fotografia do momento da empresa — Tarefas,
   // Anotações e Meta Financeira são coisas vivas que mudam a qualquer momento
   // (o usuário pode apagar uma tarefa amanhã), então não fazem sentido
   // "congeladas" num PDF já impresso. Ficam só no bloco de notas avulso.
   // O que continua fazendo sentido no relatório é o Ponto de Equilíbrio
   // (um número calculado, não uma lista editável).
   const pontoEquilibrio = data?.pontoEquilibrio || null;
   const caixaMinimo = data?.caixaMinimo || null;
   const temPlanoDeAcao = !!pontoEquilibrio;
   // A Página 4 (Análise Técnica Avançada ou APEX) agora sempre existe —
   // Ponto de Equilíbrio e Caixa Mínimo tornaram os cards sempre presentes
   // (com teaser quando bloqueados), então a numeração das páginas
   // seguintes (Veredito/Anotações) sempre soma essa página.
   const temAnaliseAvancada = true;

   // 🔥 NOVO: controla se as páginas de Projeção Estocástica e Oportunidades
   // entram no relatório, e recalcula a numeração de todas as páginas que
   // vêm depois — em vez de deixar "Página 4 de 5" hardcoded e errado.
   const temEstocastica = !!(leituraCurto || leituraMedio || leituraLongo || (horizontesLiberados && horizontesLiberados.length > 0));
   const temOportunidades = !!oportunidadesIA;
   // (números de página movidos pra depois de calcular as páginas de
   // continuação do parecer — ver mais abaixo, perto de paginasContinuacaoSuper)
   const romanVeredito = temOportunidades ? 'VI' : 'V';
   const romanAnotacoes = temOportunidades ? 'VII' : 'VI';

   // 🔥 CORREÇÃO: as páginas do parecer cortavam o array de insights em
   // posições FIXAS — um resquício de quando o parecer era sempre o
   // gerarInsights() estático, que sempre produzia exatamente 6 blocos.
   // O parecer real da IA pode ter um número diferente de seções, e cada
   // seção pode ter um tamanho bem diferente uma da outra. Ver mais abaixo
   // (perto de "distribuirComContinuacao") como isso é resolvido agora:
   // corte por caracteres + páginas de continuação automáticas.

   // Converte o texto corrido da IA (com "## títulos" e "**negrito**") em
   // parágrafos com estilo inline — mesmo princípio do formatarParecerIA
   // usado nas telas, só que adaptado pro estilo do PDF (que usa objetos
   // de style, não classes Tailwind).
   const formatarTextoIAPdf = (texto) => {
      if (!texto) return null;
      const linhas = texto.split('\n').map(l => l.trim()).filter(l => l && l !== '---');
      return linhas.map((linha, i) => {
         const negrito = (str) => str.split(/(\*\*[^*]+\*\*)/g).map((parte, j) =>
            parte.startsWith('**') && parte.endsWith('**')
               ? <strong key={j} style={{ color: '#0f172a' }}>{parte.slice(2, -2)}</strong>
               : parte
         );
         if (linha.startsWith('## ')) {
            return <h5 key={i} style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', color: '#0f172a', margin: '14px 0 6px 0' }}>{linha.replace(/^##\s*/, '').replace(/\*\*/g, '')}</h5>;
         }
         if (linha.startsWith('# ')) {
            return <h4 key={i} style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', color: '#0f172a', margin: '16px 0 8px 0' }}>{linha.replace(/^#\s*/, '').replace(/\*\*/g, '')}</h4>;
         }
         return <p key={i} style={{ fontSize: '10px', color: '#475569', margin: '0 0 8px 0', textAlign: 'justify', lineHeight: '1.6' }}>{negrito(linha)}</p>;
      });
   };

   // 🔥 CORREÇÃO DE VERDADE (2ª tentativa não bastou): eu vinha tentando
   // calibrar um "orçamento de caracteres" pra encaixar o parecer em
   // EXATAMENTE 2 páginas fixas. O problema estrutural: por mais calibrado
   // que o número fosse, se a IA escrever mais texto do que cabe nessas 2
   // páginas, a ÚLTIMA página não tinha pra onde mandar o excedente — ela
   // só empilhava tudo e vazava por cima do rodapé, não importa o número
   // escolhido. A causa raiz não era "número errado", era "número fixo de
   // páginas". Agora, se o parecer for mais longo do que cabe nas páginas
   // dedicadas, o relatório cria PÁGINAS DE CONTINUAÇÃO sozinho — o
   // relatório cresce conforme o texto, em vez de forçar o texto a caber
   // num número fixo de páginas.
   const distribuirComContinuacao = (blocos, orcamentosFixos, orcamentoContinuacao) => {
      const paginas = [];
      let idx = 0;
      const lista = blocos || [];

      // Primeiro preenche as páginas com layout especial (cover, cards, etc),
      // respeitando o orçamento de cada uma, na ordem.
      orcamentosFixos.forEach((orcamento) => {
         const pagina = [];
         let usado = 0;
         while (idx < lista.length) {
            const bloco = lista[idx];
            const tamanho = (bloco?.t?.length || 0) + (bloco?.d?.length || 0);
            if (usado + tamanho > orcamento && pagina.length > 0) break;
            pagina.push(bloco);
            usado += tamanho;
            idx += 1;
         }
         paginas.push(pagina);
      });

      // Se sobrou parecer além do que as páginas fixas seguram, cria
      // quantas páginas de continuação forem necessárias — nunca deixa
      // conteúdo sem página pra morar.
      while (idx < lista.length) {
         const pagina = [];
         let usado = 0;
         while (idx < lista.length) {
            const bloco = lista[idx];
            const tamanho = (bloco?.t?.length || 0) + (bloco?.d?.length || 0);
            if (usado + tamanho > orcamentoContinuacao && pagina.length > 0) break;
            pagina.push(bloco);
            usado += tamanho;
            idx += 1;
         }
         paginas.push(pagina);
      }

      return paginas;
   };

   // 🔥 Rede de segurança: mesmo com o orçamento por página acima, um
   // ÚNICO bloco gigante (a IA escreveu um parágrafo enorme numa seção só)
   // ainda poderia estourar sozinho. Trunca com "..." nesse caso raro —
   // garante que NUNCA mais vaza por cima do rodapé, custe o que custar.
   const truncarComSeguranca = (texto, max) => {
      if (!texto || texto.length <= max) return texto;
      return texto.slice(0, max).replace(/\s+\S*$/, '') + '…';
   };
   // A IA às vezes escreve "- ação 1. - ação 2." na mesma linha: o PDF não tem lista, então vira frases corridas.
   const semHifenDeLista = (t) => {
      const texto = String(t || '').trim();
      if (!texto.startsWith('- ')) return texto; // só mexe em textos escritos como lista
      const quebra = String.fromCharCode(10);
      return texto.slice(2).split(quebra + '- ').join(' ').split(' - ').join(' ').split(quebra).join(' ');
   };
   const insightsSeguros = (insights || []).map(item => ({ ...item, d: truncarComSeguranca(semHifenDeLista(item.d), 700) }));

   // Orçamentos RECALIBRADOS com números reais medidos nos PDFs de teste
   // (antes eram estimativas; agora contei caracteres de verdade nos
   // blocos que vazaram e nos que couberam certinho):
   //  - Master pág.1: tinha 1850, ainda vazou com 4 blocos (~1860
   //    caracteres) — o real é bem menor, porque a página já carrega
   //    Status Vital + 2 cards. Baixado pra 1300; depois pra 1050 (3 blocos de ~1100 caracteres, com o card de EBITDA ajustado de 2 linhas, tocavam a linha do rodapé).
   //  - Master pág.2: 2100 funcionou certinho no teste (4 blocos, ~1790
   //    caracteres, sobrou espaço) — mantido.
   //  - Super  pág.2: 1850 funcionou certinho no teste (4 blocos, ~1860
   //    caracteres, coube certinho) — mantido.
   //  - Super  pág.3: tinha 1300, vazou com 3 blocos (~1290 caracteres) —
   //    essa página carrega o card grande de Equity Value + 2 cards,
   //    sobra pouco espaço mesmo. Baixado pra 850.
   const paginasParecerMaster = distribuirComContinuacao(insightsSeguros, [1050, 2350], 2600);
   const blocosMasterPag1 = paginasParecerMaster[0] || [];
   const blocosMasterPag2 = paginasParecerMaster[1] || [];
   const paginasContinuacaoMaster = paginasParecerMaster.slice(2); // só existe se o parecer for muito longo

   const paginasParecerSuper = distribuirComContinuacao(insightsSeguros, [1850, 850], 2600);
   const blocosSuperPag2 = paginasParecerSuper[0] || [];
   const blocosSuperPag3 = paginasParecerSuper[1] || [];
   const paginasContinuacaoSuper = paginasParecerSuper.slice(2); // só existe se o parecer for muito longo

   // 🔥 CORRIGIDO — TEXTO DA PROJEÇÃO ESTOCÁSTICA CORTADO E RODAPÉ
   // SOBREPOSTO: a página de Projeção Estocástica (Curto/Médio/Longo Prazo)
   // sempre forçou os 3 grupos (cada um com sua tabela de cenários + leitura
   // da IA) numa ÚNICA página de altura fixa. Como a leitura de cada
   // horizonte vem da IA (tamanho variável) e os 3 grupos juntos já ocupam
   // mais espaço do que uma página A4 comporta mesmo no caso "normal", o
   // conteúdo do último grupo (Longo Prazo) ficava cortado pelo
   // `overflow: hidden` da página E, ao mesmo tempo, invadia visualmente a
   // área do rodapé (que é posicionado fixo no fim da página, sem saber que
   // o conteúdo acima cresceu). Mesma causa raiz do parecer (ver
   // `distribuirComContinuacao` acima) — a correção é a mesma: medir o
   // "custo" de cada grupo e criar páginas de continuação automaticamente
   // quando não couber tudo numa página só, em vez de forçar um número fixo.
   const gruposEstocastica = [
      { titulo: 'Curto Prazo', meses: [3, 6], texto: leituraCurto },
      { titulo: 'Médio Prazo', meses: [12], texto: leituraMedio },
      { titulo: 'Longo Prazo', meses: [24, 36], texto: leituraLongo },
   ].map(g => ({ ...g, liberado: g.meses.some(m => horizontesLiberados.includes(m)) }));

   // 🔥 RECALIBRADO com altura real medida no PDF exportado (não mais
   // estimativa): a 1ª calibração (unidades de caractere) superestimava
   // tanto o custo de cada grupo que só 1 cabia por página, mesmo sobrando
   // ~700px em branco antes do rodapé — exatamente o espaço vazio
   // reportado. Agora o custo é medido direto em PIXELS de altura real:
   //  - Card com dados: padding+título+divisor ≈ 78px fixos + 60px por
   //    linha de horizonte (medido: card de 1 linha = 147px, de 2 linhas =
   //    208px → 61px por linha extra, bate com o cálculo).
   //  - Texto da IA: ~16px por linha, ~113 caracteres por linha no
   //    tamanho/fonte usado.
   //  - Caixa "bloqueado" (plano não libera esse horizonte): fixa e pequena.
   const ESTOCASTICA_TEXTO_MAX = 2200; // mesmo limite usado na renderização abaixo
   const alturaTextoIA = (texto) => {
      if (!texto) return 0;
      const len = Math.min(texto.length, ESTOCASTICA_TEXTO_MAX);
      return Math.ceil(len / 113) * 16 + 10; // +10 do padding-top do divisor
   };
   const custoGrupoEstocasticoPx = (g) => {
      if (!g.liberado) return 70; // caixa de "bloqueado" — pequena, sempre cabe várias
      const linhas = g.meses.filter(m => horizontesLiberados.includes(m)).length;
      return 78 + (linhas * 60) + alturaTextoIA(g.texto) + 22; // +22 = gap entre cards
   };
   const distribuirGruposComContinuacao = (grupos, orcamentoPrimeiraPx, orcamentoContinuacaoPx) => {
      const paginas = [];
      let idx = 0;
      const empacotar = (orcamento) => {
         const pagina = [];
         let usado = 0;
         while (idx < grupos.length) {
            const custo = custoGrupoEstocasticoPx(grupos[idx]);
            if (usado + custo > orcamento && pagina.length > 0) break;
            pagina.push(grupos[idx]);
            usado += custo;
            idx += 1;
         }
         paginas.push(pagina);
      };
      empacotar(orcamentoPrimeiraPx);
      while (idx < grupos.length) empacotar(orcamentoContinuacaoPx);
      return paginas;
   };
   // Espaço real disponível pra cards nesta página = altura útil do A4
   // (1131 - 120 de padding topo/base) menos cabeçalho e, na 1ª página, o
   // parágrafo de introdução — medido: header ≈ 35+30, intro ≈ 18+25.
   // Uma margem de segurança de ~40px cobre pequenas variações de
   // renderização entre navegadores/fontes.
   const paginasEstocasticaTodas = temEstocastica ? distribuirGruposComContinuacao(gruposEstocastica, 860, 900) : [];
   const paginasContinuacaoEstocastica = paginasEstocasticaTodas.slice(1);

   // 🔥 Números de página do Super, agora considerando as páginas de
   // continuação do parecer E da Projeção Estocástica (se existirem). Sem
   // elas: página 4 é APEX/Análise Avançada, como sempre foi. Com elas,
   // tudo desloca.
   const paginaApexOuAnalise = 4 + paginasContinuacaoSuper.length;
   const paginaEstocastica = paginaApexOuAnalise + 1;
   const paginaOportunidades = temOportunidades ? paginaEstocastica + paginasContinuacaoEstocastica.length + 1 : null;
   const paginaVeredito = temOportunidades ? paginaEstocastica + paginasContinuacaoEstocastica.length + 2 : paginaEstocastica + paginasContinuacaoEstocastica.length + 1;
   const paginaAnotacoes = paginaVeredito + 1;
   const totalPaginasSuper = paginaAnotacoes;

   const dataAtual = new Date().toLocaleDateString('pt-BR');
   const levNum = parseFloat(alavancagem.replace(',', '.'));
   const levColor = levNum > 4.5 ? '#ef4444' : (levNum > 2.5 ? '#f97316' : '#10b981');

   // ==========================================
   // 🎨 LÓGICA DE MARCA (WHITE-LABEL)
   // ==========================================
   const primaryColor = isRealConsultor && wlName && wlColor ? wlColor : '#334155';
   const companyTitle = isRealConsultor && wlName ? wlName : 'Phoenix OS Executive';

   let statusColor = '#10b981';
   let statusBg = '#f0fdf4';
   let StatusIcon = ShieldCheck;

   // Um ícone por gravidade (antes Atenção/Crítico/Falência dividiam o
   // mesmo triângulo, e o Super 360 nem usava ícone de status — sempre um
   // "pulso", mesmo com a empresa crítica).
   if (status?.label === 'Atenção') {
      statusColor = '#f97316';
      statusBg = '#fff7ed';
      StatusIcon = AlertTriangle;
   } else if (status?.label === 'Crítico') {
      statusColor = '#ef4444';
      statusBg = '#fef2f2';
      StatusIcon = ShieldAlert;
   } else if (status?.label === 'Falência Iminente') {
      statusColor = '#dc2626';
      statusBg = '#fef2f2';
      StatusIcon = XOctagon;
   }

   // 🔥 CORRIGIDO (2026-09-30): ícone + texto do status ficavam
   // desalinhados em TODOS os PDFs (reproduzido com html2pdf: o motor
   // desenha o texto ~9px mais baixo que o layout HTML/flex prevê, e isso
   // varia com a fonte do aparelho — nenhum "ajuste de px" é confiável).
   // Solução: desenhar ícone e texto DENTRO DE UM ÚNICO SVG. O navegador
   // rasteriza o SVG inteiro de uma vez, então o alinhamento vertical é
   // exato (linha de base calculada pela altura das maiúsculas) e
   // independe do html2canvas posicionar texto.
   const renderStatusLinha = (fontSize, iconSize) => {
      const h = Math.max(iconSize, fontSize) + 8;
      const cy = h / 2;
      const baseline = cy + fontSize * 0.358;
      return (
         <svg width={560} height={h} viewBox={`0 0 560 ${h}`} style={{ display: 'block' }}>
            <StatusIcon x={0} y={cy - iconSize / 2} size={iconSize} color={statusColor} />
            <text x={iconSize + 14} y={baseline} fontFamily="Arial, Helvetica, sans-serif" fontSize={fontSize} fontWeight="900" fill={statusColor}>
               {String(status?.label || 'Analisado').toUpperCase()}
            </text>
         </svg>
      );
   };

   // Estilo Base do A4
   const a4PageStyle = {
      width: '800px', height: '1131px', backgroundColor: '#ffffff', boxSizing: 'border-box',
      padding: '60px 80px', fontFamily: 'Arial, sans-serif', color: '#0f172a',
      position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden'
   };

   const totalMasterPages = 2 + paginasContinuacaoMaster.length + (userPlan === 'ENTRY' ? 1 : 0);

   const PremiumLogo = () => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
         <div style={{ width: '40px', height: '40px', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '20px', height: '20px', border: '2px solid #ffffff' }}></div>
         </div>
         <div>
            <h1 style={{ fontSize: '24px', fontWeight: '900', letterSpacing: '1px', margin: 0, color: '#0f172a' }}>PHOENIX<span style={{ color: '#64748b' }}>OS</span></h1>
            <p style={{ fontSize: '10px', color: '#64748b', letterSpacing: '2px', textTransform: 'uppercase', margin: '2px 0 0 0', fontWeight: 'bold' }}>Intelligence Memorandum</p>
         </div>
      </div>
   );

   const renderVeredito = () => {
      const parecer = gerarParecer(
         { ebitdaFormatted: (data?.ebitdaAjustado !== undefined && data?.ebitdaAjustado !== null ? formatFix(data.ebitdaAjustado) : ebitda), ebitdaEhAjustado: data?.ebitdaAjustado !== undefined && data?.ebitdaAjustado !== null && Math.abs(Number(data.ebitdaAjustado) - Number(data.ebitda)) > 0.005, alavancagem, margin },
         isConsultor,
         empresa,
         segmento
      );

      const introTexto = isConsultor
         ? `Com base na auditoria algorítmica realizada na estrutura de capital e fluxo de caixa livre da ${empresa}, operando sob as dinâmicas do setor de ${segmento}, o comitê técnico ${isRealConsultor && wlName ? `da ${companyTitle}` : 'do Phoenix OS®'} emite o seguinte Parecer Executivo Definitivo:`
         : `Aqui está um resumo simples da situação da ${empresa} pra você entender rápido e decidir com segurança:`;

      const titulo1 = isConsultor ? '1. DIAGNÓSTICO DE LIQUIDEZ:' : '1. Como está o caixa da empresa:';
      const titulo2 = isConsultor ? '2. EFICIÊNCIA OPERACIONAL E OPEX:' : '2. A empresa está lucrando bem?';
      const titulo3 = isConsultor ? '3. RESOLUÇÃO ESTRATÉGICA (PLANO 90 DIAS):' : '3. O que fazer nos próximos 90 dias:';

      return (
         <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <p style={{ fontSize: '11px', color: '#475569', lineHeight: '1.6', textAlign: 'justify', margin: 0 }}>
               {introTexto}
            </p>
            <p style={{ fontSize: '11px', color: '#475569', lineHeight: '1.6', textAlign: 'justify', margin: 0 }}>
               <strong>{titulo1}</strong> {parecer.liquidez}
            </p>
            <p style={{ fontSize: '11px', color: '#475569', lineHeight: '1.6', textAlign: 'justify', margin: 0 }}>
               <strong>{titulo2}</strong> {parecer.eficiencia}
            </p>

            {/* INJEÇÃO DE COR DA MARCA NA BARRA LATERAL DO ITEM 3 */}
            <div style={{ backgroundColor: '#f1f5f9', padding: '20px', borderLeft: `4px solid ${'#0f172a'}`, marginTop: '10px' }}>
               <p style={{ fontSize: '11px', color: '#0f172a', fontWeight: 'bold', lineHeight: '1.6', textAlign: 'justify', margin: 0 }}>
                  <strong>{titulo3}</strong> {parecer.resolucao}
               </p>
            </div>
         </div>
      );
   };

   return (
      <div style={{ position: 'absolute', top: '-10000px', left: '-10000px', zIndex: -9999 }}>

         {/* ========================================================= */}
         {/* DOSSIÊ MASTER                                             */}
         {/* ========================================================= */}
         <div ref={basicRef}>

            {/* MASTER - PÁGINA 1 */}
            <div style={a4PageStyle}>
               {/* CABEÇALHO DINÂMICO (LOGO & CORES) */}
               <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `2px solid ${'#e2e8f0'}`, paddingBottom: '20px', marginBottom: '35px' }}>
                  <div>
                     {/* Master nunca usa whitelabel — sempre a marca neutra Phoenix OS */}
                     <PremiumLogo />
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                     <p style={{ fontSize: '12px', fontWeight: '900', margin: '0 0 5px 0', textTransform: 'uppercase', color: 'inherit' }}>PLANO {userPlan}</p>
                     <p style={{ fontSize: '10px', color: '#64748b', margin: 0, fontWeight: 'bold' }}>DATA REF: {dataAtual}</p>
                  </div>
               </div>

               <h2 style={{ fontSize: '38px', fontWeight: '900', margin: '0 0 10px 0', letterSpacing: '-1px', color: '#0f172a' }}>{empresa}</h2>
               <p style={{ fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', color: '#64748b', marginBottom: '30px' }}>Análise Executiva: {segmento}</p>

               {/* STATUS VITAL REINTEGRADO AO MASTER */}
               {/* 🔥 CORRIGIDO: o ícone ficava centralizado (alignItems: 'center')
                   contra o bloco INTEIRO de texto — rótulo pequeno (11px) + valor
                   grande (28px) empilhados. Como as duas linhas têm alturas bem
                   diferentes, o centro do bloco não coincide com o centro visual do
                   valor grande, e o ícone parecia "flutuar" desalinhado em relação
                   ao texto do status. Agora o rótulo pequeno fica numa linha própria
                   por cima, e o ícone só é pareado (items-center) com a linha do
                   valor — aí sim os dois têm a mesma altura de referência. */}
               <div style={{ backgroundColor: statusBg, border: `1px solid ${statusColor}40`, borderLeft: `8px solid ${statusColor}`, padding: '25px', marginBottom: '30px' }}>
                  <p style={{ fontSize: '11px', color: '#0f172a', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 8px 0', letterSpacing: '1px' }}>Status Vital da Operação</p>
                  {renderStatusLinha(28, 32)}
               </div>

               {/* DADOS FINANCEIROS */}
               <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px', marginBottom: '40px' }}>
                  <div style={{ border: '1px solid #cbd5e1', padding: '30px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                     {(() => {
                        // EBITDA AJUSTADO é a base de alavancagem, valuation, saque e coberturas; quando há custo
                        // não recorrente, mostra ele em destaque e o do período logo abaixo (a conta fecha na tela).
                        const temAjuste = data?.ebitdaAjustado !== undefined && data?.ebitdaAjustado !== null && Math.abs(Number(data.ebitdaAjustado) - Number(data.ebitda)) > 0.005;
                        return (<>
                           <p style={{ fontSize: '10px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '10px' }}>{temAjuste ? 'EBITDA Mensal Ajustado' : 'EBITDA Mensal'}</p>
                           <p style={{ fontSize: '24px', fontWeight: '900', margin: 0, color: '#0f172a', whiteSpace: 'nowrap' }}>R$ {formatFix(temAjuste ? data.ebitdaAjustado : data?.ebitda)}</p>
                           {temAjuste && <p style={{ fontSize: '9px', color: '#94a3b8', margin: '8px 0 0 0' }}>EBITDA do período R$ {formatFix(data?.ebitda)} + custos não recorrentes</p>}
                        </>);
                     })()}
                  </div>
                  <div style={{ border: '1px solid #cbd5e1', padding: '30px', borderLeft: `6px solid ${levColor}`, borderRadius: '4px' }}>
                     <p style={{ fontSize: '10px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '10px' }}>Alavancagem</p>
                     <p style={{ fontSize: '24px', fontWeight: '900', margin: 0, color: levColor, whiteSpace: 'nowrap' }}>{String(data?.alavancagem ?? '').replace('.', ',')}x</p>
                  </div>
               </div>

               {/* PARECER TÉCNICO INICIAL */}
               <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '25px', borderBottom: `1px solid ${'#e2e8f0'}`, paddingBottom: '10px', color: '#0f172a' }}>
                     Parecer Técnico Inicial (Parte 1/2)
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                     {blocosMasterPag1.map((item, i) => (
                        <div key={i} style={{ borderLeft: `2px solid ${'#cbd5e1'}`, paddingLeft: '15px' }}>
                           <h4 style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 5px 0', color: '#0f172a' }}>{item.t}</h4>
                           <p style={{ fontSize: '11px', color: '#475569', margin: 0, textAlign: 'justify', lineHeight: '1.5' }}>{item.d}</p>
                        </div>
                     ))}
                  </div>
               </div>

               {/* RODAPÉ DINÂMICO */}
               <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', borderTop: `1px solid ${'#cbd5e1'}`, paddingTop: '20px' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', textAlign: 'justify', margin: 0, lineHeight: '1.4' }}>{DISCLAIMER_FULL}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginTop: '6px' }}>
                     <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0, whiteSpace: 'nowrap' }}>Página 1 | Phoenix OS Executive</p>
                     <AuditStamp />
                  </div>
               </div>
            </div>

            {/* MASTER - PÁGINA 2 */}
            <div style={a4PageStyle}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '18px', borderBottom: '2px solid #0f172a', paddingBottom: '15px', marginBottom: '30px' }}>
                  <div style={{ flexShrink: 0 }}>
                     {/* Master nunca usa whitelabel — sempre a marca neutra Phoenix OS */}
                     <PremiumLogo />
                  </div>
                  <h3 style={{ fontSize: '11px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center', flex: 1 }}>I. Performance Operacional e Resiliência</h3>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 }}>Página 02</span>
               </div>

               <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '25px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', color: '#0f172a' }}>Parecer Técnico Inicial (Parte 2/2)</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                     {blocosMasterPag2.map((item, i) => (
                        <div key={i} style={{ borderLeft: '2px solid #cbd5e1', paddingLeft: '15px' }}>
                           <h4 style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 5px 0', color: '#0f172a' }}>{item.t}</h4>
                           <p style={{ fontSize: '11px', color: '#475569', margin: 0, textAlign: 'justify', lineHeight: '1.5' }}>{item.d}</p>
                        </div>
                     ))}
                  </div>

                  <div style={{ marginTop: '24px', padding: '14px 22px', backgroundColor: '#f8fafc', borderRadius: '4px', borderLeft: '3px solid #334155' }}>
                     <p style={{ fontSize: '9px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 8px 0', color: '#0f172a' }}>Nota Metodológica</p>
                     <p style={{ fontSize: '10px', color: '#64748b', margin: 0, lineHeight: '1.6', textAlign: 'justify' }}>
                        Este parecer foi gerado por análise automatizada com base nos dados financeiros informados pelo usuário (faturamento, custos, dívidas e prazos operacionais). Os índices de alavancagem, margem e valuation seguem metodologia de múltiplos de mercado por setor. Para uma análise completa com dados avançados (custo da dívida, ciclo financeiro e capital de giro), consulte a versão Dossiê Estratégico 360º.
                     </p>
                  </div>
               </div>

               <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', textAlign: 'justify', margin: 0, lineHeight: '1.4' }}>{DISCLAIMER_FULL}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginTop: '6px' }}>
                     <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0, whiteSpace: 'nowrap' }}>Página 2 de {totalMasterPages} | Phoenix OS® Auditoria</p>
                     <AuditStamp />
                  </div>
               </div>
            </div>

            {/* MASTER - PÁGINAS DE CONTINUAÇÃO (só existem se o parecer for
                mais longo do que cabe nas 2 páginas fixas acima — página
                simples, sem os cards de dados, só header + parecer). */}
            {paginasContinuacaoMaster.map((blocosPagina, idxContinuacao) => (
               <div key={`master-continuacao-${idxContinuacao}`} style={a4PageStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '18px', borderBottom: '2px solid #0f172a', paddingBottom: '15px', marginBottom: '30px' }}>
                     <div style={{ flexShrink: 0 }}><PremiumLogo /></div>
                     <h3 style={{ fontSize: '11px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center', flex: 1 }}>I. Performance Operacional e Resiliência (continuação)</h3>
                     <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 }}>Página {String(3 + idxContinuacao).padStart(2, '0')}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        {blocosPagina.map((item, i) => (
                           <div key={i} style={{ borderLeft: '2px solid #cbd5e1', paddingLeft: '15px' }}>
                              <h4 style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 5px 0', color: '#0f172a' }}>{item.t}</h4>
                              <p style={{ fontSize: '11px', color: '#475569', margin: 0, textAlign: 'justify', lineHeight: '1.5' }}>{item.d}</p>
                           </div>
                        ))}
                     </div>
                  </div>
                  <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', textAlign: 'justify', margin: 0, lineHeight: '1.4' }}>{DISCLAIMER_FULL}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginTop: '6px' }}>
                     <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0, whiteSpace: 'nowrap' }}>Página {String(3 + idxContinuacao).padStart(2, '0')} de {totalMasterPages} | Phoenix OS® Auditoria</p>
                     <AuditStamp />
                  </div>
               </div>
               </div>
            ))}

            {/* MASTER - PÁGINA 3 (SÓ ENTRY) */}
            {userPlan === 'ENTRY' && (
               <div style={{ ...a4PageStyle, backgroundColor: '#f8fafc' }}>

                  {/* CABEÇALHO WHITE-LABEL DINÂMICO - PÁGINA 3 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '18px', borderBottom: '2px solid #0f172a', paddingBottom: '15px', marginBottom: '30px' }}>
                     <div style={{ flexShrink: 0 }}>
                        {/* Master nunca usa whitelabel — sempre a marca neutra Phoenix OS */}
                        <PremiumLogo />
                     </div>
                     <h3 style={{ fontSize: '11px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center', flex: 1 }}>Análises Avançadas</h3>
                     <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 }}>Página 03</span>
                  </div>

                  <div style={{ marginBottom: '30px' }}>
                     <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#0f172a', margin: '0 0 10px 0' }}>Análises de Auditoria Avançada Bloqueadas</h2>
                     <p style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.5', margin: 0 }}>O plano atual fornece uma visão macro da operação. Para estruturação de metas precisas de Turnaround, proteção de caixa do CFO Virtual e avaliação para M&A (Valuation), recomenda-se avaliar a evolução do plano.</p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>
                     {[
                        { t: 'Simulador de Turnaround IA', d: 'Cálculo algorítmico de metas comerciais e corte de despesas (OPEX) milimétricas para atingir a zona de alta performance do seu nicho de atuação.' },
                        { t: 'CFO Virtual Preditivo', d: 'Previsão preditiva de ruptura de caixa, cálculo seguro de retirada de lucros para os sócios e dimensionamento oficial do fundo de reserva de emergência.' },
                        { t: 'Valuation Corporativo & Governança', d: 'Cálculo do Enterprise Value (Valor da Empresa) baseado em múltiplos de mercado atualizados e auditoria do Score de Prontidão para Fusões e Aquisições (M&A).' }
                     ].map((m, idx) => (
                        <div key={idx} style={{ border: '1px dashed #cbd5e1', padding: '30px', borderRadius: '8px', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', gap: '25px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                           <Lock size={28} color={"#94a3b8"} />
                           <div style={{ flex: 1 }}>
                              <h4 style={{ fontSize: '14px', fontWeight: '900', margin: '0 0 8px 0', color: '#0f172a' }}>{m.t}</h4>
                              <p style={{ fontSize: '11px', color: '#64748b', margin: 0, lineHeight: '1.5' }}>{m.d}</p>
                           </div>
                        </div>
                     ))}
                  </div>

                  <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', borderTop: `1px solid ${'#cbd5e1'}`, paddingTop: '20px' }}>
                     <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página 3 de 3 | Phoenix OS Executive</p>
                     <AuditStamp />
                  </div>
               </div>
            )}
         </div>

         {/* ========================================================= */}
         {/* SUPER RELATÓRIO 360º                                      */}
         {/* ========================================================= */}
         <div ref={superRef}>

            {/* 360 - PÁGINA 1 */}
            <div style={{ ...a4PageStyle, padding: '120px 80px', justifyContent: 'center' }}>
               <div style={{ position: 'absolute', top: '80px', left: '80px' }}>
                  {isConsultor && isRealConsultor && wlName ? (
                     <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {wlLogo && <img src={wlLogo} alt="Logo" style={{ height: '40px', objectFit: 'contain' }} />}
                        <h1 style={{ fontSize: wlLogo ? '24px' : '32px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', margin: 0, color: primaryColor }}>
                           {companyTitle}
                        </h1>
                     </div>
                  ) : (
                     <PremiumLogo />
                  )}
               </div>

               <div style={{ borderLeft: '8px solid #0f172a', paddingLeft: '40px', marginTop: '40px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase', color: '#64748b', letterSpacing: '4px' }}>Auditoria & Valuation</p>
                  <h1 style={{ fontSize: '72px', fontWeight: '900', lineHeight: '1.05', margin: '20px 0', letterSpacing: '-2px', color: '#0f172a' }}>DOSSIÊ<br />ESTRATÉGICO<br />360º</h1>
               </div>

               <div style={{ marginTop: '80px', paddingLeft: '48px' }}>
                  <p style={{ fontSize: '16px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '1px' }}>Entidade Analisada:</p>
                  <h3 style={{ fontSize: '38px', fontWeight: '900', margin: '5px 0', color: '#0f172a', letterSpacing: '-1px' }}>{empresa}</h3>
                  <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#475569', marginTop: '10px' }}>NICHO OPERACIONAL: {segmento}</p>
                  <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '30px', maxWidth: '450px', lineHeight: '1.6', textAlign: 'justify' }}>Este documento compila a auditoria analítica avançada gerada pelo motor de inteligência artificial do Phoenix OS®, contemplando análise de stress de liquidez, governança corporativa e avaliação econômica (Valuation) com metodologia de múltiplos de mercado.</p>
               </div>

               <div style={{ position: 'absolute', bottom: '80px', left: '80px', right: '80px', borderTop: '4px solid #0f172a', paddingTop: '30px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                     <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>DATA BASE: {dataAtual}</p>
                     <p style={{ fontSize: '14px', fontWeight: '900', color: '#ef4444', letterSpacing: '1px', margin: 0 }}>ESTRITAMENTE CONFIDENCIAL</p>
                  </div>
                  <AuditStamp />
               </div>
            </div>

            {/* 360 - PÁGINA 2 */}
            <div style={a4PageStyle}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '30px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                     {isConsultor && (
                        <span style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', color: primaryColor }}>
                           {companyTitle}
                        </span>
                     )}
                     <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                        I. Performance Operacional e Resiliência
                     </h3>
                  </div>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página 02</span>
               </div>

               {/* STATUS VITAL ADICIONADO AO SUPER 360 */}
               {/* 🔥 CORRIGIDO — mesmo problema do Master (ver comentário lá): ícone
                   pareado só com a linha do valor, não com o bloco inteiro. */}
               <div style={{ backgroundColor: statusBg, border: `1px solid ${statusColor}40`, borderLeft: `8px solid ${statusColor}`, padding: '30px', marginBottom: '30px' }}>
                  <p style={{ fontSize: '12px', color: '#0f172a', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 10px 0', letterSpacing: '1px' }}>Veredito de Saúde Financeira</p>
                  {renderStatusLinha(32, 36)}
               </div>

               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '40px' }}>
                  <div style={{ border: '1px solid #cbd5e1', padding: '25px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                     <p style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', minHeight: '26px' }}>{(data?.ebitdaAjustado !== undefined && data?.ebitdaAjustado !== null && Math.abs(Number(data.ebitdaAjustado) - Number(data.ebitda)) > 0.005) ? 'EBITDA Mensal Ajustado' : 'EBITDA Mensal'}</p>
                     {(() => {
                        const temAjuste = data?.ebitdaAjustado !== undefined && data?.ebitdaAjustado !== null && Math.abs(Number(data.ebitdaAjustado) - Number(data.ebitda)) > 0.005;
                        return (<>
                           <p style={{ fontSize: '24px', fontWeight: '900', margin: 0, color: '#0f172a', whiteSpace: 'nowrap' }}>R$ {formatFix(temAjuste ? data.ebitdaAjustado : data?.ebitda)}</p>
                           {temAjuste && <p style={{ fontSize: '9px', color: '#94a3b8', margin: '8px 0 0 0' }}>Período R$ {formatFix(data?.ebitda)} + custos não recorrentes</p>}
                        </>);
                     })()}
                  </div>
                  <div style={{ border: '1px solid #cbd5e1', padding: '25px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                     <p style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', minHeight: '26px' }}>Margem Operacional</p>
                     <p style={{ fontSize: '24px', fontWeight: '900', margin: 0, color: '#0f172a' }}>{String(data?.margin ?? '').replace('.', ',')}%</p>
                  </div>
                  <div style={{ border: '1px solid #cbd5e1', padding: '25px', backgroundColor: '#f8fafc', borderRadius: '4px', borderLeft: `6px solid ${levColor}` }}>
                     <p style={{ fontSize: '10px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', minHeight: '26px' }}>Alavancagem</p>
                     <p style={{ fontSize: '24px', fontWeight: '900', margin: 0, color: levColor }}>{String(data?.alavancagem ?? '').replace('.', ',')}x</p>
                  </div>
               </div>

               <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '30px', textTransform: 'uppercase' }}>Auditoria Forense de Risco</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                     {blocosSuperPag2.map((item, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '30px' }}>
                           <h5 style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', color: '#0f172a', margin: 0, borderLeft: '2px solid #cbd5e1', paddingLeft: '10px' }}>{item.t}</h5>
                           <p style={{ fontSize: '11px', color: '#475569', margin: 0, textAlign: 'justify', lineHeight: '1.6' }}>{item.d}</p>
                        </div>
                     ))}
                  </div>
               </div>

               <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página 2 de {totalPaginasSuper} | Phoenix OS® Auditoria Forense</p>
                  <AuditStamp />
               </div>
            </div>

            {/* 360 - PÁGINA 3 */}
            <div style={a4PageStyle}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '40px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                     {isConsultor && (
                        <span style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', color: primaryColor }}>
                           {companyTitle}
                        </span>
                     )}
                     <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                        II. Engenharia Financeira & Valuation Corporativo
                     </h3>
                  </div>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página 03</span>
               </div>

               <div style={{ border: '2px solid #0f172a', padding: '40px', marginBottom: '40px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
                  <p style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', letterSpacing: '2px' }}>Patrimônio Líquido Estimado (Equity Value)</p>
                  <h4 style={{ fontSize: `${Math.max(28, 56 - Math.max(0, String(formatFix(data?.equity)).length - 13) * 2.5)}px`, fontWeight: '900', margin: '15px 0', letterSpacing: '-1px', color: '#0f172a', whiteSpace: 'nowrap' }}>R$ {formatFix(data?.equity)}</h4>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '50px', borderTop: '1px solid #cbd5e1', paddingTop: '20px', marginTop: '20px' }}>
                     <div>
                        <p style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', margin: '0 0 5px 0' }}>Enterprise Value (Gross)</p>
                        <p style={{ fontSize: '16px', fontWeight: '900', color: '#0f172a', margin: 0, whiteSpace: 'nowrap' }}>R$ {valuation}</p>
                     </div>
                     <div>
                        <p style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', margin: '0 0 5px 0' }}>Dívida Onerosa Subtraída</p>
                        <p style={{ fontSize: '16px', fontWeight: '900', color: '#ef4444', margin: 0, whiteSpace: 'nowrap' }}>- R$ {formatFix(data?.debt)}</p>
                     </div>
                  </div>
               </div>

               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '40px' }}>
                  <div style={{ padding: '30px', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
                     <h5 style={{ fontSize: '11px', fontWeight: '900', margin: '0 0 10px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>LIMITES DE SAQUE (DIVIDENDOS)</h5>
                     <p style={{ fontSize: '24px', fontWeight: '900', margin: '0 0 10px 0', color: '#0f172a', whiteSpace: 'nowrap' }}>R$ {formatFix(data?.safeDividends)}</p>
                     <p style={{ fontSize: '10px', color: '#475569', lineHeight: '1.5', margin: 0, textAlign: 'justify' }}>Teto matemático máximo anual recomendado para retiradas dos sócios sem comprometer a liquidez operacional e as obrigações.</p>
                  </div>
                  <div style={{ padding: '30px', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
                     <h5 style={{ fontSize: '11px', fontWeight: '900', margin: '0 0 10px 0', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>RISCO PREDITIVO DE RUPTURA</h5>
                     <p style={{ fontSize: '24px', fontWeight: '900', margin: '0 0 10px 0', color: data?.ruptureRisk > 50 ? '#ef4444' : '#0f172a' }}>{Math.round(data?.ruptureRisk || 0)}%</p>
                     <p style={{ fontSize: '10px', color: '#475569', lineHeight: '1.5', margin: 0, textAlign: 'justify' }}>Probabilidade de inadimplência técnica nos próximos 12 meses frente à relação Dívida/EBITDA atual.</p>
                  </div>
               </div>

               <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', marginBottom: '30px', textTransform: 'uppercase' }}>Auditoria de Despesas e Avaliação</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                     {blocosSuperPag3.map((item, i) => (
                        <div key={i} style={{ borderLeft: '4px solid #0f172a', paddingLeft: '20px' }}>
                           <h5 style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', color: '#0f172a', margin: '0 0 8px 0' }}>{item.t}</h5>
                           <p style={{ fontSize: '11px', color: '#475569', margin: 0, textAlign: 'justify', lineHeight: '1.6' }}>{item.d}</p>
                        </div>
                     ))}
                  </div>
               </div>

               <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página 3 de {totalPaginasSuper} | Phoenix OS® Corporate Advisory</p>
                  <AuditStamp />
               </div>
            </div>

            {/* 360 - PÁGINAS DE CONTINUAÇÃO (só existem se o parecer for
                mais longo do que cabe nas páginas 2 e 3 acima). */}
            {paginasContinuacaoSuper.map((blocosPagina, idxContinuacao) => (
               <div key={`super-continuacao-${idxContinuacao}`} style={a4PageStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '30px' }}>
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {isConsultor && (
                           <span style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', color: primaryColor }}>
                              {companyTitle}
                           </span>
                        )}
                        <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                           II. Engenharia Financeira & Valuation Corporativo (continuação)
                        </h3>
                     </div>
                     <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página {String(4 + idxContinuacao).padStart(2, '0')}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        {blocosPagina.map((item, i) => (
                           <div key={i} style={{ borderLeft: '4px solid #0f172a', paddingLeft: '20px' }}>
                              <h5 style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', color: '#0f172a', margin: '0 0 8px 0' }}>{item.t}</h5>
                              <p style={{ fontSize: '11px', color: '#475569', margin: 0, textAlign: 'justify', lineHeight: '1.6' }}>{item.d}</p>
                           </div>
                        ))}
                     </div>
                  </div>
                  <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                     <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página {String(4 + idxContinuacao).padStart(2, '0')} de {totalPaginasSuper} | Phoenix OS® Corporate Advisory</p>
                     <AuditStamp />
                  </div>
               </div>
            ))}

            {/* 360 - PÁGINA 4: ANÁLISE DE SENSIBILIDADE APEX */}
            {apexData && apexData.ebitdaBase > 0 && (
               <div style={a4PageStyle}>
                  <div style={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'flex-start',
                     borderBottom: '2px solid #0f172a',
                     paddingBottom: '12px',
                     marginBottom: '30px'
                  }}>
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {isConsultor && (
                           <span style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', color: primaryColor }}>
                              {companyTitle}
                           </span>
                        )}
                        <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                           III. Matriz de Sensibilidade APEX™
                        </h3>
                     </div>
                     <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página {String(paginaApexOuAnalise).padStart(2, '0')}</span>
                  </div>

                  {/* PARÂMETROS BASE */}
                  <div style={{
                     display: 'grid',
                     gridTemplateColumns: '1fr 1fr 1fr',
                     gap: '15px',
                     marginBottom: '25px',
                     backgroundColor: '#f8fafc',
                     padding: '20px',
                     borderRadius: '4px',
                     border: '1px solid #e2e8f0'
                  }}>
                     <div>
                        <p style={{ fontSize: '9px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                           EBITDA Mensal (base do cálculo)
                        </p>
                        <p style={{ fontSize: '16px', fontWeight: '900', color: '#0f172a', margin: 0 }}>
                           R$ {formatFix(apexData.ebitdaBase)}
                        </p>
                     </div>
                     <div>
                        <p style={{ fontSize: '9px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                           Múltiplo Aplicado
                        </p>
                        <p style={{ fontSize: '16px', fontWeight: '900', color: '#0f172a', margin: 0 }}>
                           {apexData.multipleCurrent || apexData.multiple || 5.5}x
                        </p>
                     </div>
                     <div>
                        <p style={{ fontSize: '9px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                           WACC Aplicado
                        </p>
                        <p style={{ fontSize: '16px', fontWeight: '900', color: '#0f172a', margin: 0 }}>
                           {apexData.waccCurrent || 12.5}%
                        </p>
                     </div>
                  </div>

                  {/* MATRIZ DE SENSIBILIDADE */}
                  <div style={{ marginBottom: '25px' }}>
                     <p style={{ fontSize: '10px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>
                        Matriz de Sensibilidade — Equity Value (R$)
                     </p>

                     {/* Tabela de Sensibilidade */}
                     <div style={{ border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        {/* Cabeçalho da Tabela */}
                        <div style={{
                           display: 'grid',
                           gridTemplateColumns: '1fr 1fr 1fr 1fr',
                           backgroundColor: '#0f172a',
                           color: '#ffffff',
                           padding: '10px 15px',
                           fontSize: '9px',
                           fontWeight: 'bold',
                           textTransform: 'uppercase',
                           letterSpacing: '0.5px'
                        }}>
                           <div>EBITDA (Var.)</div>
                           <div>Múltiplo</div>
                           <div>WACC</div>
                           <div style={{ textAlign: 'right' }}>Equity Value</div>
                        </div>

                        {/* Linhas da Matriz - 9 cenários */}
                        {(() => {
                           const baseEbitda = apexData.ebitdaBase;
                           const baseMultiple = apexData.multipleCurrent || apexData.multiple || 5.5;
                           const baseWacc = apexData.waccCurrent || 12.5;
                           const debt = apexData.debt || 0;
                           const variations = [-0.10, 0, 0.10];
                           const rows = [];

                           variations.forEach(ebVar => {
                              variations.forEach(waccVar => {
                                 const mult = baseMultiple;
                                 const wacc = baseWacc + (waccVar * 5);
                                 const ebitda = baseEbitda * (1 + ebVar);
                                 const ev = Math.max(0, (ebitda * mult * 12) * (1 - (wacc - 12.5) / 100) - debt);
                                 const isCurrent = ebVar === 0 && waccVar === 0;
                                 rows.push({ ebVar, mult, wacc, ebitda, ev, isCurrent });
                              });
                           });

                           // Ordenar por EBITDA, depois WACC
                           rows.sort((a, b) => a.ebitda - b.ebitda || a.wacc - b.wacc);

                           return rows.map((row, idx) => (
                              <div key={idx} style={{
                                 display: 'grid',
                                 gridTemplateColumns: '1fr 1fr 1fr 1fr',
                                 padding: '8px 15px',
                                 backgroundColor: row.isCurrent ? '#f0fdf4' : (idx % 2 === 0 ? '#f8fafc' : '#ffffff'),
                                 borderTop: idx > 0 ? '1px solid #e2e8f0' : 'none',
                                 fontSize: '11px'
                              }}>
                                 <div style={{ color: row.ebVar < 0 ? '#ef4444' : row.ebVar > 0 ? '#10b981' : '#0f172a', fontWeight: row.isCurrent ? 'bold' : 'normal' }}>
                                    {row.ebVar > 0 ? '+' : ''}{(row.ebVar * 100).toFixed(0)}%
                                 </div>
                                 <div style={{ color: '#0f172a', fontWeight: row.isCurrent ? 'bold' : 'normal' }}>
                                    {row.mult.toFixed(1)}x
                                 </div>
                                 <div style={{ color: '#0f172a', fontWeight: row.isCurrent ? 'bold' : 'normal' }}>
                                    {row.wacc.toFixed(1)}%
                                 </div>
                                 <div style={{
                                    textAlign: 'right',
                                    color: row.isCurrent ? '#10b981' : '#0f172a',
                                    fontWeight: row.isCurrent ? 'bold' : 'normal'
                                 }}>
                                    R$ {row.ev.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                 </div>
                              </div>
                           ));
                        })()}
                     </div>
                  </div>

                  {/* RANGE DE VALUATION */}
                  <div style={{
                     display: 'grid',
                     gridTemplateColumns: '1fr 1fr 1fr',
                     gap: '15px',
                     marginBottom: '25px'
                  }}>
                     {(() => {
                        const baseEbitda = apexData.ebitdaBase;
                        const baseMultiple = apexData.multipleCurrent || apexData.multiple || 5.5;
                        const baseWacc = apexData.waccCurrent || 12.5;
                        const debt = apexData.debt || 0;
                        const variations = [-0.10, 0, 0.10];
                        let values = [];

                        variations.forEach(ebVar => {
                           variations.forEach(waccVar => {
                              const mult = baseMultiple;
                              const wacc = baseWacc + (waccVar * 5);
                              const ebitda = baseEbitda * (1 + ebVar);
                              const ev = Math.max(0, (ebitda * mult * 12) * (1 - (wacc - 12.5) / 100) - debt);
                              values.push(ev);
                           });
                        });

                        const min = Math.min(...values);
                        const max = Math.max(...values);
                        const avg = values.reduce((a, b) => a + b, 0) / values.length;

                        return (
                           <>
                              <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                 <p style={{ fontSize: '8px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                                    Mínimo
                                 </p>
                                 <p style={{ fontSize: '12px', fontWeight: '900', color: '#ef4444', margin: 0, whiteSpace: 'nowrap' }}>
                                    R$ {min.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                 </p>
                              </div>
                              <div style={{ backgroundColor: '#f0fdf4', padding: '15px', borderRadius: '4px', border: '2px solid #10b981', textAlign: 'center' }}>
                                 <p style={{ fontSize: '8px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                                    Média
                                 </p>
                                 <p style={{ fontSize: '13px', fontWeight: '900', color: '#10b981', margin: 0, whiteSpace: 'nowrap' }}>
                                    R$ {avg.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                 </p>
                              </div>
                              <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '4px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                 <p style={{ fontSize: '8px', color: '#64748b', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 4px 0', letterSpacing: '0.5px' }}>
                                    Máximo
                                 </p>
                                 <p style={{ fontSize: '12px', fontWeight: '900', color: '#10b981', margin: 0, whiteSpace: 'nowrap' }}>
                                    R$ {max.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                 </p>
                              </div>
                           </>
                        );
                     })()}
                  </div>

                  {/* NOTA EXPLICATIVA */}
                  <div style={{
                     backgroundColor: '#f1f5f9',
                     padding: '15px',
                     borderRadius: '4px',
                     borderLeft: `4px solid ${isConsultor ? primaryColor : '#0f172a'}`
                  }}>
                     <p style={{ fontSize: '9px', color: '#475569', lineHeight: '1.6', margin: 0, textAlign: 'justify' }}>
                        <strong>Nota Metodológica:</strong> A Matriz de Sensibilidade APEX™ apresenta 9 cenários combinando variações de EBITDA (-10%, 0%, +10%) e WACC (±5 pontos percentuais). O valor em <strong style={{ color: '#10b981' }}>destaque</strong> representa o cenário base utilizado no cálculo do Equity Value. Esta análise auxilia na compreensão do impacto de mudanças nas premissas macroeconômicas sobre o valuation da empresa.
                     </p>
                  </div>

                  {/* RODAPÉ */}
                  <div style={{
                     position: 'absolute',
                     bottom: '60px',
                     left: '80px',
                     right: '80px',
                     paddingTop: '20px'
                  }}>
                     <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página {String(paginaApexOuAnalise).padStart(2, '0')} de {totalPaginasSuper} | Phoenix OS® Matriz de Sensibilidade APEX™</p>
                     <AuditStamp />
                  </div>
               </div>
            )}

            {/* 360 - PÁGINA 4 (GESTOR) — Análise Técnica Avançada, no lugar do APEX que é exclusivo do Consultor.
                Ponto de Equilíbrio e Caixa Mínimo aparecem SEMPRE nessa página — com o valor real quando
                o plano libera, ou como um teaser de upgrade quando não libera. Isso complementa a página
                (nunca deixa buraco), sem nunca remover os outros 4 indicadores que já existiam aqui. */}
            {(!apexData) && (() => {
               const debt = data?.debt || 0;
               const custoNaoRecorrente = data?.custoNaoRecorrente || 0;
               const ebitdaRaw = data?.ebitda || 0;

               // Recalcula tudo aqui na página, direto dos dados brutos — não depende de outro lugar do código.
               const ebitdaAjustadoCalc = ebitdaRaw + custoNaoRecorrente;
               const custoDividaPct = Number(data?.custoDivida) || 0;
               const custoServicoDividaCalc = debt > 0 ? debt * (custoDividaPct / 100) : 0;
               // Juros e dívida de curto prazo são valores do ANO; o EBITDA é mensal (x12).
               const coberturaJurosCalc = custoServicoDividaCalc > 0 ? ((ebitdaAjustadoCalc * 12) / custoServicoDividaCalc).toFixed(2) : null;
               const dividaCP = data?.dividaCurtoPrazo || 0;
               const coberturaCPCalc = dividaCP > 0 ? ((ebitdaAjustadoCalc * 12) / dividaCP).toFixed(2) : null;

               const temPrazos = data?.prazoRecebimento && data?.prazoPagamento;
               const cicloCliente = temPrazos ? Number(data.prazoRecebimento) - Number(data.prazoPagamento) : null;
               const cicloSetor = getSectorCycleBenchmark(data?.sector);
               const ncgValor = data?.ncg || 0;

               const ebitdaAjustadoStr = formatFix(ebitdaAjustadoCalc);
               const ncgStr = formatFix(Math.abs(ncgValor));
               const custoDividaStr = formatFix(custoServicoDividaCalc);

               // Agora que os 6 cards mostram valor real (nunca mais teaser curto),
               // a altura entre eles fica mais uniforme — dá pra respirar um
               // pouco mais que o compacto extremo, mantendo tudo numa página.
               // Reduzido de novo: 6 cards empilhados + nota + leitura ainda
               // estourava por pouco no final da página. 12px de padding cabe
               // tudo direitinho numa página só.
               const cardEstilo = { padding: '10px 22px', borderRadius: '4px' };
               const valorEstilo = { fontSize: '17px', fontWeight: '900', margin: '0 0 4px 0', whiteSpace: 'nowrap' };
               const labelEstilo = { fontSize: '9px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' };
               const descEstilo = { fontSize: '9px', color: '#475569', lineHeight: '1.3', margin: 0 };

               return (
                  <div style={a4PageStyle}>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '8px', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '13px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                           III. Análise Técnica Avançada
                        </h3>
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página {String(paginaApexOuAnalise).padStart(2, '0')}</span>
                     </div>

                     <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>

                        {/* PONTO DE EQUILÍBRIO — sempre calculado; quando o plano não libera,
                            mostra um teaser em vez de sumir, pra sempre completar a página. */}
                        <div style={{ ...cardEstilo, border: '1px solid #e2e8f0', borderLeft: '5px solid #64748b', backgroundColor: '#f8fafc' }}>
                           <p style={{ ...labelEstilo, color: '#334155' }}>Ponto de Equilíbrio</p>
                           {pontoEquilibrio ? (
                              <>
                                 <p style={{ ...valorEstilo, color: '#0f172a' }}>R$ {formatFix(pontoEquilibrio)}</p>
                                 <p style={descEstilo}>Faturamento mínimo mensal pra cobrir todos os custos.</p>
                              </>
                           ) : (
                              <p style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>Disponível a partir do protocolo Plus.</p>
                           )}
                        </div>

                        {/* CAIXA MÍNIMO MENSAL — idem, usa o pró-labore informado no cadastro. */}
                        <div style={{ ...cardEstilo, border: '1px solid #e2e8f0', borderLeft: '5px solid #64748b', backgroundColor: '#f8fafc' }}>
                           <p style={{ ...labelEstilo, color: '#334155' }}>Caixa Mínimo Mensal</p>
                           {caixaMinimo ? (
                              <>
                                 <p style={{ ...valorEstilo, color: '#0f172a' }}>R$ {formatFix(caixaMinimo)}</p>
                                 <p style={descEstilo}>Custos fixos + pró-labore dos sócios.</p>
                              </>
                           ) : (
                              <p style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>Disponível a partir do protocolo Plus.</p>
                           )}
                        </div>

                        {/* EBITDA AJUSTADO — card azul */}
                        <div style={{ ...cardEstilo, border: '1px solid #bfdbfe', borderLeft: '5px solid #3b82f6', backgroundColor: '#eff6ff' }}>
                           <p style={{ ...labelEstilo, color: '#1e40af' }}>EBITDA Ajustado (Normalizado)</p>
                           <p style={{ ...valorEstilo, color: '#1e3a8a' }}>R$ {ebitdaAjustadoStr}</p>
                           <p style={descEstilo}>EBITDA (R$ {ebitda}) + custo não recorrente informado.</p>
                        </div>

                        {/* NCG — 🔥 CORREÇÃO: antes mostrava "Necessidade de Capital de
                            Giro: -R$ X" com texto "favorável" embaixo — um número
                            negativo sempre soa como falta de dinheiro, mesmo com o
                            texto dizendo o contrário. Agora, quando é favorável, o
                            card muda de nome pra "Folga de Capital de Giro" e mostra
                            o valor positivo — sem sinal de menos nenhum. */}
                        <div style={{ ...cardEstilo, border: `1px solid ${ncgValor < 0 ? '#a7f3d0' : '#e2e8f0'}`, borderLeft: `5px solid ${ncgValor < 0 ? '#10b981' : '#64748b'}`, backgroundColor: ncgValor < 0 ? '#f0fdf4' : '#f8fafc' }}>
                           <p style={{ ...labelEstilo, color: '#334155' }}>{ncgValor < 0 ? 'Folga de Capital de Giro' : 'Necessidade de Capital de Giro'}</p>
                           <p style={{ ...valorEstilo, color: ncgValor < 0 ? '#059669' : '#0f172a' }}>
                              R$ {ncgStr}
                           </p>
                           <p style={descEstilo}>
                              {ncgValor < 0 ? 'Ciclo favorável: fornecedores financiam a operação, sobrando caixa em vez de exigir capital de giro.' : 'Caixa entre pagar fornecedores e receber dos clientes.'}
                           </p>
                           {temPrazos && (
                              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                                 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#475569' }}>
                                    <span>Seu ciclo: <strong style={{ color: '#0f172a' }}>{cicloCliente}d</strong></span>
                                    <span>Setor: <strong style={{ color: '#0f172a' }}>{cicloSetor}d</strong></span>
                                 </div>
                              </div>
                           )}
                        </div>

                        {/* CUSTO DA DÍVIDA — card laranja, ou teaser explicando quando falta dado */}
                        <div style={{ ...cardEstilo, border: '1px solid #fed7aa', borderLeft: '5px solid #f97316', backgroundColor: '#fff7ed' }}>
                           <p style={{ ...labelEstilo, color: '#9a3412' }}>Custo Anual da Dívida (Juros)</p>
                           {custoServicoDividaCalc > 0 ? (
                              <>
                                 <p style={{ ...valorEstilo, color: '#c2410c' }}>R$ {custoDividaStr}</p>
                                 <p style={descEstilo}>
                                    {coberturaJurosCalc > 0 ? `EBITDA Ajustado cobre ${String(coberturaJurosCalc).replace('.', ',')}x esse custo.` : 'Alerta: caixa insuficiente pra cobrir os juros.'}
                                 </p>
                              </>
                           ) : (
                              <p style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>Sem dívida ou custo da dívida informado no cadastro.</p>
                           )}
                        </div>

                        {/* COBERTURA DE CURTO PRAZO — verde/vermelho, ou teaser quando falta dado */}
                        <div style={{ ...cardEstilo, border: `1px solid ${coberturaCPCalc !== null && coberturaCPCalc < 1 ? '#fecaca' : '#a7f3d0'}`, borderLeft: `5px solid ${coberturaCPCalc !== null && coberturaCPCalc < 1 ? '#ef4444' : '#10b981'}`, backgroundColor: coberturaCPCalc !== null && coberturaCPCalc < 1 ? '#fef2f2' : '#f0fdf4' }}>
                           <p style={{ ...labelEstilo, color: '#334155' }}>Cobertura da Dívida de Curto Prazo</p>
                           {coberturaCPCalc !== null ? (
                              <>
                                 <p style={{ ...valorEstilo, color: coberturaCPCalc < 1 ? '#dc2626' : '#059669' }}>{String(coberturaCPCalc).replace('.', ',')}x</p>
                                 <p style={descEstilo}>
                                    {coberturaCPCalc < 1 ? 'Alerta: EBITDA não cobre a dívida que vence este ano.' : 'Quantas vezes o EBITDA cobre a dívida deste ano.'}
                                 </p>
                              </>
                           ) : (
                              <p style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>Sem dívida de curto prazo informada no cadastro.</p>
                           )}
                        </div>
                     </div>

                     <div style={{ marginTop: '8px', padding: '8px 14px', backgroundColor: '#f8fafc', borderRadius: '4px', borderLeft: `3px solid ${isRealConsultor && wlName ? primaryColor : '#0f172a'}` }}>
                        <p style={{ fontSize: '8px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 2px 0', color: '#0f172a' }}>Nota Metodológica</p>
                        <p style={{ fontSize: '8px', color: '#64748b', margin: 0, lineHeight: '1.3', textAlign: 'justify' }}>
                           Ponto de Equilíbrio e Caixa Mínimo são calculados automaticamente a partir dos dados básicos da empresa. Os demais indicadores desta página dependem dos dados técnicos avançados informados (custo não recorrente, prazos de recebimento/pagamento, dívida de curto prazo e custo da dívida) — quanto mais completos, mais precisa é a leitura.
                        </p>
                     </div>

                     {/* LEITURA CONSOLIDADA — 🔥 NOVO: parágrafo gerado a partir dos
                         próprios números desta página, junta as pontas soltas (folga
                         de caixa, ciclo financeiro, cobertura de dívida) numa leitura
                         só. Substitui o espaço em branco que sobrava quando poucos
                         cards apareciam com um conteúdo que agrega valor de verdade,
                         em vez de só esticar a página visualmente. */}
                     <div style={{ marginTop: '8px', marginBottom: '90px', padding: '18px 22px', border: '1px solid #e2e8f0', borderRadius: '4px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <p style={{ fontSize: '9px', fontWeight: '900', textTransform: 'uppercase', margin: '0 0 8px 0', color: '#0f172a' }}>Leitura Consolidada</p>
                        <p style={{ fontSize: '11px', color: '#334155', margin: 0, lineHeight: '1.7', textAlign: 'justify' }}>
                           {(() => {
                              const frases = [];

                              if (pontoEquilibrio && caixaMinimo) {
                                 const folga = caixaMinimo - pontoEquilibrio;
                                 frases.push(
                                    folga >= 0
                                       ? `O caixa mínimo mensal necessário (R$ ${formatFix(caixaMinimo)}) já supera o ponto de equilíbrio operacional em R$ ${formatFix(folga)} — ou seja, mesmo cobrindo todos os custos, sobra pouca margem para sustentar o pró-labore dos sócios sem pressão adicional sobre o caixa.`
                                       : `O ponto de equilíbrio operacional (R$ ${formatFix(pontoEquilibrio)}) fica acima do caixa mínimo mensal, o que indica alguma folga entre cobrir os custos fixos e sustentar a retirada dos sócios.`
                                 );
                              }

                              if (ncgValor < 0) {
                                 frases.push('O ciclo financeiro é favorável: os prazos negociados com fornecedores financiam parte da operação, reduzindo a pressão sobre o caixa de giro.');
                              } else if (temPrazos) {
                                 frases.push(`O ciclo financeiro exige capital de giro próprio, já que o prazo de recebimento (${data.prazoRecebimento} dias) é maior que o de pagamento a fornecedores (${data.prazoPagamento} dias).`);
                              }

                              if (coberturaCPCalc !== null) {
                                 frases.push(
                                    coberturaCPCalc < 1
                                       ? 'A cobertura da dívida de curto prazo está abaixo de 1x — sinal de atenção, já que o EBITDA ajustado sozinho não é suficiente para quitar os compromissos que vencem este ano.'
                                       : `A dívida de curto prazo está confortavelmente coberta (${String(coberturaCPCalc).replace('.', ',')}x o EBITDA ajustado), o que reduz o risco de aperto de caixa no curto prazo.`
                                 );
                              }

                              if (custoServicoDividaCalc > 0 && coberturaJurosCalc > 0) {
                                 frases.push(`Os juros da dívida consomem uma fração pequena da geração de caixa (cobertura de ${String(coberturaJurosCalc).replace('.', ',')}x), o que não compromete a operação no cenário atual.`);
                              }

                              if (frases.length === 0) {
                                 frases.push('Os dados técnicos avançados (custo não recorrente, prazos, dívida de curto prazo) ainda não foram informados no cadastro. Complete-os para uma leitura mais precisa da saúde financeira da operação.');
                              }

                              return frases.join(' ');
                           })()}
                        </p>
                     </div>

                     <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                        <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página {String(paginaApexOuAnalise).padStart(2, '0')} de {totalPaginasSuper} | Phoenix OS® Análise Técnica Avançada</p>
                        <AuditStamp />
                     </div>
                  </div>
               );
            })()}

            {/* 360 - PÁGINA 5 (NOVA): PROJEÇÃO POR IA ESTOCÁSTICA
                Antes essa análise só existia na tela — o cliente que
                recebia o PDF impresso/exportado nunca via isso. Mostra os
                3 cenários (pessimista/provável/otimista) pra cada
                horizonte liberado no plano, com a leitura da IA logo
                abaixo de cada um. */}
            {temEstocastica && paginasEstocasticaTodas.map((grupos, paginaIdx) => {
               const numeroPagina = paginaEstocastica + paginaIdx;
               const ehContinuacao = paginaIdx > 0;
               return (
                  <div key={paginaIdx} style={a4PageStyle}>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '30px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                           {isConsultor && (
                              <span style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', color: primaryColor }}>
                                 {companyTitle}
                              </span>
                           )}
                           <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                              IV. Projeção por IA Estocástica{ehContinuacao ? ' (continuação)' : ''}
                           </h3>
                        </div>
                        <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página {String(numeroPagina).padStart(2, '0')} de {totalPaginasSuper}</span>
                     </div>

                     {!ehContinuacao && (
                        <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 25px 0', lineHeight: '1.6', fontStyle: 'italic' }}>
                           Não é só um retrato de hoje — é para onde a operação está indo, em três cenários por horizonte de tempo.
                        </p>
                     )}

                     <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '22px', overflow: 'hidden' }}>
                        {grupos.map((grupo, idx) => {
                           const liberado = grupo.liberado;
                           if (!liberado) {
                              return (
                                 <div key={idx} style={{ border: '1px dashed #cbd5e1', borderRadius: '4px', padding: '18px 22px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <Lock size={18} color="#94a3b8" />
                                    <p style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 'bold', margin: 0 }}>{grupo.titulo} ({grupo.meses.join(' e ')} meses) — disponível em planos superiores.</p>
                                 </div>
                              );
                           }
                           return (
                              <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '20px 24px', backgroundColor: '#f8fafc' }}>
                                 <p style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', color: '#0f172a', margin: '0 0 14px 0' }}>{grupo.titulo} ({grupo.meses.join(' e ')} meses)</p>
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                                    {grupo.meses.filter(m => horizontesLiberados.includes(m)).map((m) => {
                                       const dado = projecaoEstocastica.find(p => p.months === m);
                                       if (!dado) return null;
                                       return (
                                          <div key={m} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1fr', alignItems: 'center', gap: '10px', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '10px 14px' }}>
                                             <span style={{ fontSize: '9px', fontWeight: '900', color: '#64748b' }}>{m}m</span>
                                             <div style={{ textAlign: 'center' }}>
                                                <p style={{ fontSize: '7px', color: '#94a3b8', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 2px 0' }}>Pessimista</p>
                                                <p style={{ fontSize: '11px', fontWeight: '900', color: '#ef4444', margin: 0, whiteSpace: 'nowrap' }}>R$ {formatFix(dado.pessimista)}</p>
                                             </div>
                                             <div style={{ textAlign: 'center' }}>
                                                <p style={{ fontSize: '7px', color: '#94a3b8', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 2px 0' }}>Provável</p>
                                                <p style={{ fontSize: '11px', fontWeight: '900', color: '#0f172a', margin: 0, whiteSpace: 'nowrap' }}>R$ {formatFix(dado.provavel)}</p>
                                             </div>
                                             <div style={{ textAlign: 'center' }}>
                                                <p style={{ fontSize: '7px', color: '#94a3b8', fontWeight: 'bold', textTransform: 'uppercase', margin: '0 0 2px 0' }}>Otimista</p>
                                                <p style={{ fontSize: '11px', fontWeight: '900', color: '#10b981', margin: 0, whiteSpace: 'nowrap' }}>R$ {formatFix(dado.otimista)}</p>
                                             </div>
                                          </div>
                                       );
                                    })}
                                 </div>
                                 {grupo.texto ? (
                                    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                                       {/* 🔥 CORRIGIDO — TEXTO CORTADO SEM NECESSIDADE: antes o limite era
                                           550 caracteres mesmo depois da paginação automática (acima) já
                                           garantir espaço de sobra pra cada grupo em sua própria página.
                                           2200 é só a rede de segurança real (texto absurdamente longo),
                                           não um corte de rotina. */}
                                       {formatarTextoIAPdf(truncarComSeguranca(grupo.texto, 2200))}
                                    </div>
                                 ) : (
                                    <p style={{ fontSize: '9px', color: '#94a3b8', fontStyle: 'italic', margin: '10px 0 0 0' }}>Análise em texto ainda não gerada para este horizonte.</p>
                                 )}
                              </div>
                           );
                        })}
                     </div>

                     <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                        <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página {String(numeroPagina).padStart(2, '0')} de {totalPaginasSuper} | Phoenix OS® Projeção Estocástica</p>
                        <AuditStamp />
                     </div>
                  </div>
               );
            })}

            {/* 360 - PÁGINA 6 (NOVA): OPORTUNIDADES ESTRATÉGICAS
                Exclusiva de quem já tem esse bloco gerado (hoje, Enterprise
                Ultra no Consultor). Só aparece quando existe conteúdo —
                nunca deixa uma página em branco no meio do relatório. */}
            {temOportunidades && (
               <div style={a4PageStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '30px' }}>
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {isConsultor && (
                           <span style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', color: primaryColor }}>
                              {companyTitle}
                           </span>
                        )}
                        <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                           V. Oportunidades Estratégicas
                        </h3>
                     </div>
                     <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página {String(paginaOportunidades).padStart(2, '0')} de {totalPaginasSuper}</span>
                  </div>

                  <div style={{ flex: 1, overflow: 'hidden' }}>
                     {formatarTextoIAPdf(truncarComSeguranca(oportunidadesIA, 2800))}
                  </div>

                  <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                     <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Página {String(paginaOportunidades).padStart(2, '0')} de {totalPaginasSuper} | Phoenix OS® Oportunidades Estratégicas</p>
                     <AuditStamp />
                  </div>
               </div>
            )}

            {/* 360 - VEREDITO FINAL */}
            <div style={a4PageStyle}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '40px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                     {isConsultor && (
                        <span style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', fontStyle: 'italic', color: primaryColor }}>
                           {companyTitle}
                        </span>
                     )}
                     <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                        {romanVeredito}. Diretrizes Estratégicas e Veredito Final
                     </h3>
                  </div>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página {String(paginaVeredito).padStart(2, '0')}</span>
               </div>

               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '50px' }}>
                  <div style={{ backgroundColor: '#0f172a', color: '#fff', padding: '35px', borderRadius: '4px', textAlign: 'center' }}>
                     <h5 style={{ fontSize: '11px', fontWeight: 'bold', color: '#cbd5e1', margin: '0 0 15px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>M&A Readiness Score</h5>
                     <p style={{ fontSize: '64px', fontWeight: '900', margin: 0, lineHeight: '1' }}>{data?.score}<span style={{ fontSize: '20px', color: '#64748b' }}>/100</span></p>
                     <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '20px', lineHeight: '1.5' }}>Índice de governança e atratividade contábil para processos formais de due diligence ou sucessão estruturada.</p>
                  </div>
                  <div style={{ border: '1px solid #cbd5e1', padding: '35px', borderRadius: '4px' }}>
                     <h5 style={{ fontSize: '11px', fontWeight: '900', margin: '0 0 15px 0', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '1px' }}>Projeção de Turnaround</h5>
                     {(Number(data?.simRevenue) || 0) === 0 && (Number(data?.simCosts) || 0) === 0 ? (
                        <p style={{ fontSize: '11px', color: '#475569', lineHeight: '1.6', margin: 0 }}>
                           Nenhuma meta de virada foi simulada neste relatório. Use o Simulador de Turnaround no app para testar o efeito de vender mais ou cortar custos sobre o EBITDA mensal ajustado (hoje R$ {formatFix(data?.projectedEbitda)}).
                        </p>
                     ) : (<>
                     <p style={{ fontSize: '28px', fontWeight: '900', margin: '0 0 20px 0', color: '#0f172a', whiteSpace: 'nowrap' }}>R$ {formatFix(data?.projectedEbitda)}</p>
                     <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                           <span style={{ fontSize: '10px', color: '#475569', fontWeight: 'bold' }}>Aumento de Receita Exigido:</span>
                           <span style={{ fontSize: '10px', fontWeight: '900', color: '#10b981' }}>+{data?.simRevenue}%</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                           <span style={{ fontSize: '10px', color: '#475569', fontWeight: 'bold' }}>Redução Tática de OPEX:</span>
                           <span style={{ fontSize: '10px', fontWeight: '900', color: '#3b82f6' }}>-{data?.simCosts}%</span>
                        </div>
                     </div>
                     </>)}
                  </div>
               </div>

               <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '900', marginBottom: '20px', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px', textTransform: 'uppercase' }}>VI. Veredito Estratégico Definitivo</h4>
                  {renderVeredito()}
               </div>

               <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', textAlign: 'justify', margin: 0, lineHeight: '1.4' }}>{DISCLAIMER_FULL}</p>
                  <AuditStamp />
               </div>
            </div>

            {/* 360 - ANOTAÇÕES — sempre por último, com espaço em branco pra
                escrever à mão (reunião, próximos passos combinados, etc). */}
            <div style={a4PageStyle}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '12px', marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#0f172a', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
                     {romanAnotacoes}. Anotações
                  </h3>
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Página {String(paginaAnotacoes).padStart(2, '0')}</span>
               </div>

               <div style={{ border: '1px solid #e2e8f0', padding: '22px', borderRadius: '4px', flex: 1 }}>
                  {Array.from({ length: 18 }).map((_, i) => (
                     <div key={i} style={{ borderBottom: '1px solid #e2e8f0', height: '28px' }} />
                  ))}
               </div>

               <div style={{ position: 'absolute', bottom: '60px', left: '80px', right: '80px', paddingTop: '20px' }}>
                  <p style={{ fontSize: '8px', color: '#94a3b8', margin: 0 }}>Phoenix OS® Anotações</p>
                  <AuditStamp />
               </div>
            </div>

         </div>
      </div>
   );
}